const RANGE_CONFIG = {
  tq: { translationKey: "thisQuarter", fallback: "This Quarter" },
  pq: { translationKey: "lastQuarter", fallback: "Last Quarter" },
  tm: { translationKey: "thisMonth", fallback: "This Month" },
  pm: { translationKey: "lastMonth", fallback: "Last Month" },
  "3m": { translationKey: "last3Months", fallback: "Last 3 Months" },
  "6m": { translationKey: "last6Months", fallback: "Last 6 Months" },
  "12m": { translationKey: "last12Months", fallback: "Last 12 Months" },
  pfy: { translationKey: "lastYear", fallback: "Previous Financial Year" },
  tfy: { translationKey: "thisYear", fallback: "This Financial Year" },
  p2fy: { translationKey: null, fallback: "Previous 2 Financial Years" },
  "2fy": { translationKey: null, fallback: "2 Financial Years" },
  p3fy: { translationKey: null, fallback: "Previous 3 Financial Years" },
  "3fy": { translationKey: null, fallback: "3 Financial Years" },
  custom: { translationKey: "customRange", fallback: "Custom Range" },
};

const pad = (value) => String(value).padStart(2, "0");

const formatDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const getFirstDayOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const getLastDayOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

const getYearStart = (year) => new Date(year, 0, 1);

const getYearEnd = (year) => new Date(year, 11, 31);

export const RANGE_VALUES = Object.keys(RANGE_CONFIG);

export const isCustomRangeValue = (value) => value === "custom";

export const formatDateLabel = (dateString) => {
  if (!dateString) return "";

  return new Date(`${dateString}T00:00:00`)
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-")
    .replace(",", "");
};

export const getRangeDisplayLabel = (rangeValue, texts) => {
  const config = RANGE_CONFIG[rangeValue] || RANGE_CONFIG.custom;
  return (
    (config.translationKey ? texts?.reports?.[config.translationKey] : null) ||
    config.fallback
  );
};

export const getRangeOptions = (texts) =>
  RANGE_VALUES.map((value) => ({
    value,
    label: getRangeDisplayLabel(value, texts),
  }));

export const getRangeSelectionLabel = (rangeValue, texts, fromDate, toDate) => {
  const label = getRangeDisplayLabel(rangeValue, texts);

  if (!fromDate || !toDate) {
    return label;
  }

  return `${label} | ${formatDateLabel(fromDate)} - ${formatDateLabel(toDate)}`;
};

export const calculateDateRange = (rangeValue) => {
  const referenceDate = new Date();
  let startDate = getFirstDayOfMonth(referenceDate);
  let endDate = getLastDayOfMonth(referenceDate);
  const currentYear = referenceDate.getFullYear();

  switch (rangeValue) {
    case "3m":
      startDate = getFirstDayOfMonth(new Date(currentYear, referenceDate.getMonth() - 2, 1));
      break;
    case "6m":
      startDate = getFirstDayOfMonth(new Date(currentYear, referenceDate.getMonth() - 5, 1));
      break;
    case "12m":
      startDate = getFirstDayOfMonth(new Date(currentYear, referenceDate.getMonth() - 11, 1));
      break;
    case "pm":
      startDate = getFirstDayOfMonth(new Date(currentYear, referenceDate.getMonth() - 1, 1));
      endDate = getLastDayOfMonth(new Date(currentYear, referenceDate.getMonth() - 1, 1));
      break;
    case "tq": {
      const quarter = Math.floor(referenceDate.getMonth() / 3);
      startDate = getFirstDayOfMonth(new Date(currentYear, quarter * 3, 1));
      endDate = getLastDayOfMonth(new Date(currentYear, (quarter + 1) * 3 - 1, 1));
      break;
    }
    case "pq": {
      const previousQuarterDate = new Date(currentYear, referenceDate.getMonth() - 3, 1);
      const quarter = Math.floor(previousQuarterDate.getMonth() / 3);
      startDate = getFirstDayOfMonth(new Date(previousQuarterDate.getFullYear(), quarter * 3, 1));
      endDate = getLastDayOfMonth(
        new Date(previousQuarterDate.getFullYear(), (quarter + 1) * 3 - 1, 1)
      );
      break;
    }
    case "tfy":
      startDate = getYearStart(currentYear);
      endDate = getYearEnd(currentYear);
      break;
    case "pfy":
      startDate = getYearStart(currentYear - 1);
      endDate = getYearEnd(currentYear - 1);
      break;
    case "2fy":
      startDate = getYearStart(currentYear - 1);
      endDate = getYearEnd(currentYear);
      break;
    case "p2fy":
      startDate = getYearStart(currentYear - 2);
      endDate = getYearEnd(currentYear - 1);
      break;
    case "3fy":
      startDate = getYearStart(currentYear - 2);
      endDate = getYearEnd(currentYear);
      break;
    case "p3fy":
      startDate = getYearStart(currentYear - 3);
      endDate = getYearEnd(currentYear - 1);
      break;
    default:
      break;
  }

  return {
    fromDate: formatDate(startDate),
    toDate: formatDate(endDate),
  };
};

